import { DOOGLIE_EXTENSION_ID } from './extension';

/**
 * Whether the Dooglie extension answers in this browser, and whether a Chrome
 * update has most likely switched it off.
 *
 * A Chrome update that adds a site to the extension's permissions disables
 * the extension until its user turns it back on in chrome://extensions; the
 * extension simply stops answering, and nothing on screen says why. The app
 * shell shows a banner for that, but only to someone whose browser has had
 * the extension answer before: someone who never installed it gets the
 * ordinary "Download extension" item, not news of an update they never had.
 */

/** How long the handshake waits for an answer before calling it silence. */
export const HANDSHAKE_TIMEOUT_MS = 4000;

const SEEN_KEY = 'dooglie.extension.answered';
const DISMISSED_KEY = 'dooglie.extension.offBannerDismissed';

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the banner just shows again next load.
  }
};

/** Chrome itself, not another Chromium browser that installs from the store. */
export const isChrome = (ua: string = navigator.userAgent): boolean =>
  /Chrome\//.test(ua) && !/(Edg|OPR|Brave|SamsungBrowser)\//.test(ua);

/** The extension answered: remember it for this browser. */
export const noteExtensionAnswered = () => write(SEEN_KEY, '1');

export const extensionAnsweredBefore = (): boolean => read(SEEN_KEY) === '1';

export const dismissExtensionOffBanner = () => write(DISMISSED_KEY, '1');

export const extensionOffBannerDismissed = (): boolean => read(DISMISSED_KEY) === '1';

type SendMessage = (
  extensionId: string,
  message: unknown,
  callback: (response: unknown) => void,
) => void;

/**
 * Sends `message` to the extension and resolves whether anything answered
 * within `timeoutMs`. With the extension off, Chrome either reports an error
 * on the callback or (when no extension accepts messages from this page)
 * hides `chrome.runtime` altogether; both are silence.
 */
export const sendToExtension = (
  message: unknown,
  timeoutMs: number = HANDSHAKE_TIMEOUT_MS,
): Promise<boolean> =>
  new Promise((resolve) => {
    const runtime = (
      globalThis as { chrome?: { runtime?: { sendMessage?: SendMessage; lastError?: unknown } } }
    ).chrome?.runtime;
    if (runtime?.sendMessage == null) {
      resolve(false);
      return;
    }
    let settled = false;
    const settle = (answered: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (answered) noteExtensionAnswered();
      resolve(answered);
    };
    const timer = window.setTimeout(() => settle(false), timeoutMs);
    try {
      runtime.sendMessage(DOOGLIE_EXTENSION_ID, message, (response) => {
        if (runtime.lastError) {
          console.log('Error:', runtime.lastError);
          settle(false);
        } else {
          console.log('Response from extension:', response);
          settle(true);
        }
      });
    } catch (error) {
      console.log('Failed to send to extension:', error);
      settle(false);
    }
  });

/**
 * Whether to tell this user a Chrome update switched Dooglie off: Chrome,
 * no answer this load, an answer some time before, and not dismissed.
 */
export const shouldShowExtensionOffBanner = (answered: boolean): boolean =>
  !answered && isChrome() && extensionAnsweredBefore() && !extensionOffBannerDismissed();
